import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, Calendar, Tag } from "lucide-react";
import { Button } from "./ui/button";

export default function DocumentModal({ document, onClose }) {
  if (!document) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <AnimatePresence>
      {document && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            data-testid="document-modal-backdrop"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-2xl sm:w-full bg-[#12121a] rounded-2xl border border-[#252530] shadow-2xl z-50 overflow-hidden flex flex-col max-h-[90vh]"
            data-testid="document-modal"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-[#252530] flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00d4aa] to-[#6ee7c2] flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-[#0a0a0f]" />
                </div>
                <div>
                  <h2 
                    className="text-xl font-semibold text-[#f0f0f5]"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    data-testid="document-title"
                  >
                    {document.title}
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00d4aa]/15 border border-[#00d4aa]/30 text-xs font-medium text-[#6ee7c2]">
                      <Tag className="w-3 h-3" />
                      {document.category || "General"}
                    </span>
                    {document.created_at && (
                      <span className="flex items-center gap-1.5 text-xs text-[#606070]">
                        <Calendar className="w-3 h-3" />
                        {formatDate(document.created_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-[#606070] hover:text-[#f0f0f5] hover:bg-[#252530]"
                data-testid="document-modal-close"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <p 
                className="text-[#f0f0f5] leading-relaxed whitespace-pre-wrap"
                data-testid="document-content"
              >
                {document.content}
              </p>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-[#252530] flex justify-end">
              <Button
                onClick={onClose}
                className="bg-gradient-to-r from-[#00d4aa] to-[#6ee7c2] hover:opacity-90 text-[#0a0a0f] rounded-xl font-semibold"
                data-testid="document-modal-close-button"
              >
                Close
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
